import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { AUTOPLAY_MS, canAutoplay, hidesInactivePanels, intervalFrom, keyTarget, toggleStep, wrapIndex } from '@theme/eme-switcher-core';

class EmeSwitcher extends Component {
  #index = 0;
  #timer = null;
  #observer = null;
  #interval = AUTOPLAY_MS;
  #accordion = false;
  #openIndex = 0;
  #tablistAttributes = null;
  #state = { paused: false, hovered: false, focused: false, visible: false, manual: false };
  #dialogTab = null;

  connectedCallback() {
    super.connectedCallback();
    this.#interval = intervalFrom(this.dataset.interval);
    this.#linkPanels();
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    if (this.dataset.autoplay === 'true') {
      this.addEventListener('pointerenter', this.#handlePointerEnter);
      this.addEventListener('pointerleave', this.#handlePointerLeave);
      this.addEventListener('focusin', this.#handleFocusIn);
      this.addEventListener('focusout', this.#handleFocusOut);
      this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.25 });
      this.#observer.observe(this);
    }
    mediaQueryLarge.addEventListener('change', this.#handleBreakpoint);
    this.select(0);
    this.#syncAccordion();
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    this.#syncDialog();
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpoint);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    if (this.#dialogTab) document.documentElement.style.removeProperty('overflow');
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #tabs() {
    return this.refs.tabs ?? [];
  }

  get #panels() {
    return this.refs.panels ?? [];
  }

  get #isSteps() {
    return this.dataset.listStyle === 'steps';
  }

  get #dialog() {
    if (this.dataset.listStyle !== 'questions') return null;
    const { dialog } = this.refs;
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  select(index, event) {
    const tabs = this.#tabs;
    if (!tabs.length) return;
    if (this.#accordion) {
      this.#openIndex = event?.type === 'click' ? toggleStep(this.#openIndex, index) : wrapIndex(index, tabs.length);
      if (this.#openIndex !== -1) this.#activate(this.#openIndex);
      this.#syncExpanded();
      return;
    }
    this.#activate(index);
    if (event?.type === 'click') this.#stopAfterManualChoice();
    if (event?.type === 'click' && this.#dialog && isMobileBreakpoint()) this.#openDialog(this.#tabs[this.#index]);
  }

  toggleTranscript() {
    const panel = this.#panels[this.#index];
    const toggle = panel?.querySelector('.eme-switcher__transcript-toggle');
    this.#setTranscript(panel, toggle?.getAttribute('aria-expanded') !== 'true', { moveFocus: true });
  }

  closeDialog() {
    this.#dialog?.close();
  }

  handleDialogClick(event) {
    if (event.composedPath()[0] === this.#dialog) this.closeDialog();
  }

  #activate(index) {
    const tabs = this.#tabs;
    const previous = this.#index;
    this.#index = wrapIndex(index, tabs.length);
    if (!this.#accordion) {
      for (const [position, tab] of tabs.entries()) {
        const active = position === this.#index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      }
    }
    const hideInactive = hidesInactivePanels({ layout: this.dataset.layout, listStyle: this.dataset.listStyle });
    for (const [position, panel] of this.#panels.entries()) {
      const active = position === this.#index;
      panel.toggleAttribute('data-active', active);
      if (hideInactive) panel.hidden = !active;
      else panel.setAttribute('aria-hidden', String(!active));
    }
    if (previous !== this.#index && this.dataset.listStyle === 'questions') {
      this.#setTranscript(this.#panels[previous], false);
      this.#resetVideo(this.#panels[previous]);
    }
    this.refs.tabpanel?.setAttribute('aria-labelledby', tabs[this.#index].id);
    this.#dialog?.setAttribute('aria-labelledby', tabs[this.#index].id);
  }

  togglePlayback() {
    this.#state.paused = !this.#state.paused;
    if (!this.#state.paused) this.#state.manual = false;
    this.#syncPlayback();
    this.#schedule();
  }

  #syncPlayback() {
    const { playback } = this.refs;
    if (!(playback instanceof HTMLElement)) return;
    playback.setAttribute('aria-label', playback.dataset[this.#state.paused ? 'labelPlay' : 'labelPause'] ?? '');
    playback.toggleAttribute('data-paused', this.#state.paused);
  }

  #syncAccordion() {
    const accordion = this.#isSteps && isMobileBreakpoint();
    if (accordion === this.#accordion) return;
    const list = this.refs.tablist;
    const tabs = this.#tabs;
    if (accordion) {
      this.#tablistAttributes = {
        role: list?.getAttribute('role') ?? null,
        orientation: list?.getAttribute('aria-orientation') ?? null,
        label: list?.getAttribute('aria-label') ?? null,
        controls: tabs.map((tab) => tab.getAttribute('aria-controls')),
      };
      this.#accordion = true;
      this.#openIndex = this.#index;
      this.dataset.emeAccordion = '';
      list?.removeAttribute('role');
      list?.removeAttribute('aria-orientation');
      list?.removeAttribute('aria-label');
      const bodies = this.refs.stepBodies ?? [];
      for (const [position, tab] of tabs.entries()) {
        tab.removeAttribute('role');
        tab.removeAttribute('aria-selected');
        const body = bodies[position];
        if (body?.id) tab.setAttribute('aria-controls', body.id);
        else tab.removeAttribute('aria-controls');
        tab.tabIndex = 0;
      }
      this.#syncExpanded();
      return;
    }
    const saved = this.#tablistAttributes;
    this.#accordion = false;
    delete this.dataset.emeAccordion;
    if (saved?.role) list?.setAttribute('role', saved.role);
    if (saved?.orientation) list?.setAttribute('aria-orientation', saved.orientation);
    if (saved?.label) list?.setAttribute('aria-label', saved.label);
    for (const [position, tab] of tabs.entries()) {
      tab.setAttribute('role', 'tab');
      tab.removeAttribute('aria-expanded');
      const controls = saved?.controls[position];
      if (controls) tab.setAttribute('aria-controls', controls);
      else tab.removeAttribute('aria-controls');
    }
    this.#activate(this.#openIndex === -1 ? 0 : this.#index);
  }

  #syncExpanded() {
    for (const [position, tab] of this.#tabs.entries()) {
      tab.setAttribute('aria-expanded', String(position === this.#openIndex));
    }
  }

  #linkPanels() {
    if (this.dataset.layout !== 'tabs') return;
    const tabs = this.#tabs;
    for (const [position, panel] of this.#panels.entries()) {
      const tab = tabs[position];
      if (!tab) {
        panel.hidden = true;
        continue;
      }
      panel.id = `${tab.id}-panel`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      tab.setAttribute('aria-controls', panel.id);
    }
  }

  #setTranscript(panel, open, { moveFocus = false } = {}) {
    const toggle = panel?.querySelector('.eme-switcher__transcript-toggle');
    const transcript = panel?.querySelector('.eme-switcher__transcript-text');
    const frame = panel?.querySelector('.eme-switcher__video-frame');
    if (!(toggle instanceof HTMLElement) || !(transcript instanceof HTMLElement) || !frame) return;
    if (open) {
      const video = panel.querySelector('eme-video');
      if (video && typeof video.setPaused === 'function') video.setPaused(true);
    }
    transcript.hidden = !open;
    frame.toggleAttribute('data-transcript', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = toggle.dataset[open ? 'labelVideo' : 'labelAnswer'] ?? '';
    if (!moveFocus) return;
    if (open) {
      transcript.scrollTop = 0;
      transcript.focus();
    } else {
      toggle.focus();
    }
  }

  #syncDialog() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const mobile = isMobileBreakpoint();
    for (const tab of this.#tabs) {
      if (mobile) tab.setAttribute('aria-haspopup', 'dialog');
      else tab.removeAttribute('aria-haspopup');
    }
    if (mobile) {
      if (dialog.open && !dialog.matches(':modal')) {
        if (dialog.contains(document.activeElement)) this.#tabs[this.#index]?.focus();
        dialog.close();
      }
      return;
    }
    if (dialog.matches(':modal')) dialog.close();
    // show() would move focus into the inline panel on load; the open attribute keeps it non-modal without focusing.
    if (!dialog.open) dialog.setAttribute('open', '');
  }

  #openDialog(tab) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    this.#dialogTab = tab ?? null;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.refs.dialogClose?.focus();
  }

  #handleDialogClose = () => {
    const panel = this.#panels[this.#index];
    const tab = this.#dialogTab;
    this.#dialogTab = null;
    document.documentElement.style.removeProperty('overflow');
    this.#setTranscript(panel, false);
    this.#resetVideo(panel);
    const focus = document.activeElement;
    if (tab && (!focus || focus === document.body || this.#dialog?.contains(focus))) tab.focus();
  };

  #resetVideo(panel) {
    const video = panel?.querySelector('eme-video');
    if (video && typeof video.reset === 'function') video.reset();
  }

  #stopAfterManualChoice() {
    if (this.dataset.listStyle !== 'names' || !isMobileBreakpoint()) return;
    this.#state.manual = true;
    this.#state.paused = true;
    this.#syncPlayback();
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.dataset.autoplay !== 'true') return;
    const allowed = canAutoplay({ count: this.#tabs.length, reducedMotion: prefersReducedMotion(), mobile: this.#isSteps && isMobileBreakpoint(), ...this.#state });
    if (!allowed) return;
    this.#timer = setTimeout(() => {
      this.select(this.#index + 1);
      this.#schedule();
    }, this.#interval);
  }

  #handleBreakpoint = () => {
    this.#syncAccordion();
    this.#syncDialog();
    this.#schedule();
  };

  #handleKeydown = (event) => {
    if (this.#accordion) return;
    const tabs = this.#tabs;
    const current = tabs.indexOf(event.target);
    if (current === -1) return;
    const orientation = this.dataset.layout === 'list' ? 'vertical' : 'horizontal';
    const rtl = getComputedStyle(this).direction === 'rtl';
    const next = keyTarget(event.key, current, tabs.length, { orientation, rtl });
    if (next === null) return;
    event.preventDefault();
    this.select(next);
    this.#stopAfterManualChoice();
    tabs[next].focus();
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const position = this.#panels.findIndex((panel) => panel.contains(event.target));
    if (position !== -1) this.select(position);
  };

  #handlePointerEnter = () => {
    this.#state.hovered = true;
    this.#schedule();
  };

  #handlePointerLeave = () => {
    this.#state.hovered = false;
    this.#schedule();
  };

  #handleFocusIn = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
    this.#state.focused = true;
    this.#schedule();
  };

  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#state.focused = false;
    this.#schedule();
  };

  #handleIntersect = (entries) => {
    const entry = entries.at(-1);
    if (!entry) return;
    this.#state.visible = entry.isIntersecting;
    this.#schedule();
  };
}

if (!customElements.get('eme-switcher')) customElements.define('eme-switcher', EmeSwitcher);
